/* eslint-disable camelcase */
import SyncableModel from "./SyncableModel"
import { getRootStore } from "../utils/root-store"
import axios from "axios"
import { delay } from "../utils/delay"
import RNBluetoothClassic from "react-native-bluetooth-classic"
import moment from "moment/moment"

class AutoFeederPond extends SyncableModel {
    static name = 'AutoFeederPond'

    _fillable = [
      'id',
      'pond_id',
      'auto_feeder_address',
      'name',
      'mode',
      'debit',
      'state',
      'created_at', 'updated_at',
      'synced_at', 'deleted_at',
    ]

    _casts = {
      state: 'object'
    }

    static endpoint = '/auto_feeder_ponds'

    static async syncAll(
      params = {}, sqlParams = [], cleanAfterSync = true,
      onSyncProgressUpdate = undefined, onError = undefined
    ) {
      const { userStore: { user } } = getRootStore();
      const _params = { ...params };
      const _sqlParams = [...sqlParams];
      if (user && user.role_id === 1 && !_params.pond_id__in) {
        _params.has = 'pond.farm.farm_user';
      }
      return super.syncAll(_params, _sqlParams, cleanAfterSync, onSyncProgressUpdate, onError);
    }

    async write(command: string) {
      const writeBluetooth = async (command: string) => {
        await this.bluetoothDevice.write(command);
      }

      if (this.ip) {
        await axios.post(`http://${this.ip}/command`, `command=${command}`, {
          timeout: 15000
        })
          .catch(async (error) => {
            this.ip = null;
            throw error;
          })
        await delay(1500);
      }
      else if (this.bluetoothDevice)
        await writeBluetooth(command);
    }

    async writeUntilResponse(command: string, timeout = 15000) {
      const writeUntilResponseBluetooth = async (command: string, timeout = 15000) => {
        await this.bluetoothDevice.write(command);
        let available = 0;
        for (let i = 0; i < Math.round(timeout / 100); i++) {
          await delay(100);
          available = await this.bluetoothDevice.available().catch(() => 0)
          if (available) break;
        }
        if (!available) return null;
        await delay(1000);
        return this.bluetoothDevice.read();
      }

      if (this.ip)
        return axios.post(`http://${this.ip}/command`, `command=${command}`, {
          timeout
        }).then(({ data }) => data).catch(() => {
          this.ip = null;
          return writeUntilResponseBluetooth(command, timeout)
        })
      else if (this.bluetoothDevice) {
        return writeUntilResponseBluetooth(command, timeout)
      }
    }

    async clear() {
      if (this.bluetoothDevice) return this.bluetoothDevice.clear()
    }

    async connect(value: "bluetooth"|"wifi" = "bluetooth") {
      const state = this.getAttribute('state')
      if (value === "wifi") {
        this.ip = state?.ip
        await this.write(`;8;${moment().format('YYYY/MM/DD;HH:mm:ss')}#`).catch((e) => {
          this.ip = null;
          throw e;
        })
      } else {
        const connected = await RNBluetoothClassic.isDeviceConnected(this.getAttribute('auto_feeder_address')).catch(() => false)
        this.bluetoothDevice = connected ?
          await RNBluetoothClassic.getConnectedDevice(this.getAttribute('auto_feeder_address')).catch(() => null) :
          await RNBluetoothClassic.connectToDevice(this.getAttribute('auto_feeder_address'));
      }
    }

    async disconnect() {
      if (this.ip) this.ip = null;
      if (this.bluetoothDevice) {
        await this.bluetoothDevice.disconnect().catch(() => true);
        this.bluetoothDevice = null;
      }
    }

    getIsConnectedAttribute() {
      return !!(this.bluetoothDevice || this.ip);
    }

    setIsConnectedAttribute(value) {
      return this
    }

    getThrowDebitAttribute() {
      const state = this.getAttribute('state')
      return (state?.debit || 120) * (state?.onTime || 1);
    }

    setThrowDebitAttribute(value) {
      const state = this.getAttribute('state')
      this.setAttribute('state', {
        ...state,
        debit: Math.round(value / (state?.onTime || 1))
      })
      return this
    }

    calculateDebit(data) {
      const { condition, actualEndTime, actualIntervalPeriod, remainingQuantity, expectedQuantity } = data;
      const state = this.getAttribute('state')
      let multiplier = 1;
      if (condition === 'under') {
        if (state?.mode === 'continuous') {
          const actualEnd = moment(actualEndTime, 'HH:mm:ss');
          const start = moment(state?.startTime || '00:00:00', 'HH:mm:ss');
          const end = moment(state?.endTime || '23:59:00', 'HH:mm:ss');
          if (actualEndTime && actualEnd.isAfter(start))
            multiplier = 1 / (actualEnd.diff(start, 'seconds') / end.diff(start, 'seconds'));
        } else {
          if (actualIntervalPeriod) {
            multiplier = 1 / (actualIntervalPeriod / (state?.intervalPeriod || 60))
          }
        }
      } else {
        if (remainingQuantity && expectedQuantity && expectedQuantity > remainingQuantity) {
          multiplier = (expectedQuantity - remainingQuantity) / expectedQuantity;
        }
      }

      return Math.round(multiplier * (state?.debit || 120))
    }

    async commandToggleRun() {
      return this.write(';7#');
    }

    async commandTestRun(second) {
      return this.write(`;16;${second.toFixed(1)}#`)
    }

    async commandSetDateTime(dateTime) {
      return this.write(
        `;8;${moment(dateTime || undefined).format('YYYY/MM/DD;HH:mm:ss')}#`
      )
    }

    /**
     * @type { feed_at, quantity } feed
     * @type { time, percent, onTime, intervalPeriod } feedPerDay
     * @returns {Promise<*|null|undefined>}
     */
    async commandSetFeedPerDay(feed, feedPerDay) {
      if (!feedPerDay.time) return;
      const state = this.getAttribute('state')
      // return await device.write(
      //   `;1;1;` +
      //   `${moment(feed.feed_at).format('YYYY/MM/DD')};` +
      //   `${moment(feedPerDay.time, 'HH:mm:ss').format('HH:mm')};` +
      //   `${Number(feed.quantity / feedPerDay[i].timesPerPeriod * 1000 / feedPerDay[i].debit * feedPerDay[j].percent / 100).toFixed(2)};` +
      //   `${Number(feedPerDay[j].timesPerPeriod).toFixed(0)}#`
      // )
      const onTime = feedPerDay.onTime || state?.onTime || 1
      const debit = state?.debit || 120
      const intervalPeriod = feedPerDay.intervalPeriod || state?.intervalPeriod || 60
      const timesPerPeriod = (feed.quantity * 1000 * feedPerDay.percent / 100 / (onTime * debit));
      const intervalPerPeriod = (intervalPeriod) / timesPerPeriod;
      return await this.writeUntilResponse(
        `;1;12;` +
        `${moment(feed.feed_at).format('YYYY/MM/DD')};` +
        `${moment(feedPerDay.time, 'HH:mm:ss').format('HH:mm')};` +
        `${Number(onTime).toFixed(2)};` +
        `${Number(0.499 + timesPerPeriod).toFixed(0)};` +
        `${Number(intervalPerPeriod).toFixed(2)}#`
      )
    }

    /**
     *
     * @type { feed_at, quantity, startTime, endTime, onTime, onTimeOverride, interval, intervalOverride } feed
     * @returns {Promise<*|null|undefined>}
     */
    async commandSetFeed(feed) {
      const startTime = moment(feed.startTime, 'HH:mm:ss');
      const endTime = moment(feed.endTime, 'HH:mm:ss');
      return await this.writeUntilResponse(
        `;1;2;` +
        `${moment(feed.feed_at).format('MM/DD')};` +
        `${startTime.format('HH:mm')};` +
        `${endTime.format('HH:mm')};` +
        `${(feed.onTimeOverride || feed.onTime || 1).toFixed(2)};` +
        `${(feed.intervalOverride || feed.interval || 0.01).toFixed(2)}#`
      )
    }

    async commandOnTimeAndIntervalOverride(onTime, interval) {
      return await this.write(`;9;${onTime};${interval}#`)
    }

    async commandSaveChanges(timeout = 15000) {
      return await this.writeUntilResponse(";4#", timeout)
    }

    async commandClear() {
      return await this.writeUntilResponse(";2;1#")
    }
}

AutoFeederPond.prototype.bluetoothDevice = null
AutoFeederPond.prototype.ip = null

export default AutoFeederPond;
